import React, {useEffect, useState} from "react"
import authentication from "@irati/auth"
import styled from "styled-components"
import {useBillingUnitsQuery} from "@irati/billing/hooks/bu"
import {Hr} from "@irati/ui/src/Layout"
import {EntityType, identifier} from "@perkbox/flexi-points"
import Currency from "@irati/payments/enums/Currency"
import Select from "@irati/ui/src/FormItem/Select"
import Validation from "@irati/utils/validation"
import {Body, H2, H3} from "@irati/ui/src/Typography"
import Error from "@irati/ui/src/Loading/Error"
import OccasionsService from "@irati/recognition/services/Occasions"
import {
  useCreateRecognitionAutomationMutation,
  useUpdateRecognitionAutomationMutation,
  useUpdateRecognitionAutomationStatusMutation,
  useRecognitionAutomationsQuery
} from "@irati/recognition-automation/hooks"
import FormattedMessage, {injectIntl} from "@irati/utils/lang"
import {InjectIntlProps} from "@irati/utils/lang/injectIntl"
import {
  CelebrationTag,
  CelebrationTagKind,
  RecognitionAutomationStatus,
  RecognitionAutomationInput,
  RecognitionAutomationRule,
  RecognitionAutomation,
  RecognitionAutomationRuleType,
  RecognitionAutomationType
} from "@irati/types"
import getAutomationsQuery from "@irati/recognition-automation/apollo/query/RecognitionAutomations"
import Spacing from "@irati/ui/src/Spacing"
import {Checkbox, CustomNodeRenderer, Hidden, Input, Radio, ValidationButton} from "@irati/ui/src/FormItem"
import Flex from "@irati/ui/src/Flex"
import Button from "@irati/ui/src/Button"
import {festiveTemplateSchema} from "@irati-web/fields/poll/votes/validation"
import Form from "@irati/ui/src/Form"
import Message, {OnChangeProps} from "@irati-web/fields/recognition/Message"
import FullPreview from "@irati-web/wallet/src/components/TemplatePreview/FullPreview"
import teamName from "@irati/identity/utils/team/teamName"
import UserBudgetSelector from "../../components/UserBudgetSelector"
import AutomationsRules from "../../components/AutomationsRules"
import AutomationConfirmationModal, {Operation} from "../../components/AutomationConfirmationModal"
import {emptyAutomation, emptyRewardAutomation, getRecognitionAutomationInput} from "./helpers"
import CardOption from "../../components/AutomationsRules/CardOptions"
import mainTheme from "@irati/ui/src/global/theme"
import {Option} from "@irati/ui/src/FormItem/Radio/Radio"
import {RadioInput} from "@irati/ui/src/FormItem/Radio/styled"

const CustomRadio = styled(Radio)`
  ${RadioInput} {
    visibility: hidden;
  }
`

interface Props extends InjectIntlProps {
  recognitionAutomation?: RecognitionAutomation
}
enum SenderNameOptions {
  ADMIN_NAME,
  CUSTOM
}
const TEMPLATE_MESSAGE_MAX_LENGTH = 256
const SENDER_NAME_MAX_LENGTH = 40
const FormHandler: React.FC<Props> = (props) => {
  const {formatMessage, intl, recognitionAutomation} = props
  const [updateStatus] = useUpdateRecognitionAutomationStatusMutation()
  const [senderNameOption, setSenderNameOption] = useState(
    recognitionAutomation?.senderName ? SenderNameOptions.CUSTOM : SenderNameOptions.ADMIN_NAME
  )
  const {data: alreadyCreatedRecognitionAutomations} = useRecognitionAutomationsQuery(
    {
      occasionId: props?.match?.params?.uuid
    },
    {
      skip: !!recognitionAutomation
    }
  )
  const [createRecognitionAutomation, {error: createError, errorMsg: createErrorMsg, data: recognitionAutomationCreated}] =
    useCreateRecognitionAutomationMutation({
      refetchQueries: [{query: getAutomationsQuery, variables: {occasionId: props?.match?.params?.uuid}}]
    })

  const [updateRecognitionAutomation, {error: updateError, errorMsg: updateErrorMsg, data: recognitionAutomationUpdated}] =
    useUpdateRecognitionAutomationMutation({
      refetchQueries: [{query: getAutomationsQuery, variables: {occasionId: recognitionAutomation?.occasionId}}]
    })
  const {data: billingUnits} = useBillingUnitsQuery(
    {
      businessId: authentication.business().uuid
    },
    {
      skip: !!recognitionAutomation,
      fetchPolicy: "cache-and-network"
    }
  )
  const initialAutomationState = recognitionAutomation
    ? getRecognitionAutomationInput(recognitionAutomation)
    : {
        ...emptyAutomation,
        occasionId: props.match.params.uuid
      }
  const [automation, setAutomation] = useState<RecognitionAutomationInput>(initialAutomationState)
  const [submit, setSubmit] = useState()
  const [isSaveOp, setIsSaveOp] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [rewardTemplate, setRewardTemplate] = useState<CelebrationTag>()
  useEffect(() => {
    OccasionsService.getCelebrationsTags(CelebrationTagKind.Automation).then(selectDefault)
  }, [])
  useEffect(() => {
    const businessUnitsToShow = billingUnits?.filter(
      ({teamId}) => !alreadyCreatedRecognitionAutomations?.some(({regionId}) => regionId === teamId)
    )
    if (businessUnitsToShow?.length === 1) {
      handleSelectCountry(businessUnitsToShow[0].teamId!)
    }
  }, [billingUnits, alreadyCreatedRecognitionAutomations])

  const handleSelectCountry = (regionId: string) => {
    setAutomation({
      ...automation,
      regionId
    })
  }
  const selectDefault = (celebrationTags: CelebrationTag[]) => {
    if (!automation.occasionId || !celebrationTags) {
      return
    }

    const defaultFound = celebrationTags.find(({uuid}) => automation.occasionId === uuid)

    if (!defaultFound) {
      return
    }

    setRewardTemplate(defaultFound)
  }
  const onWalletChange = (tenantId: string, budgetId: string, useUnassigned: boolean) => {
    const tenantWallet = identifier({
      currency: Currency.RP,
      entityId: tenantId,
      entityType: EntityType.Reward
    })
    setAutomation({
      ...automation,
      walletId: tenantWallet,
      budget: {
        categoryId: budgetId,
        useUnassigned
      }
    })
  }
  const onMessageChange = (data: OnChangeProps) => {
    setAutomation({
      ...automation,
      message: data.message,
      companyCelebrationId: data.occasion?.uuid,
      companyValueIds: data.companyValues?.map(({uuid}) => uuid)
    })
  }
  const onSubmit = async (recognitionAutomationStatus: RecognitionAutomationStatus, isSave = false) => {
    const mutationCall = recognitionAutomation?.uuid ? updateRecognitionAutomation : createRecognitionAutomation
    if (recognitionAutomation?.uuid && recognitionAutomationStatus !== recognitionAutomation.status) {
      await updateStatus({
        variables: {
          status: recognitionAutomationStatus,
          uuid: recognitionAutomation.uuid!
        }
      })
    }
    const automationToSend: RecognitionAutomationInput = {
      ...automation,
      recognitionAutomationStatus,
      senderName: automation.senderName,
      ...(automation.recognitionAutomationType === RecognitionAutomationType.RecognitionOnly
        ? Object.keys(emptyRewardAutomation).reduce((acc, key) => ({...acc, [key]: null}), {})
        : {})
    }
    const submitPromise = mutationCall({
      variables: {
        recognitionAutomation: automationToSend,
        uuid: recognitionAutomation?.uuid
      }
    })
    setSubmit(submitPromise)
    setIsSaveOp(isSave)
    return submitPromise
  }
  const handleConfirmAutomation = () => {
    setAutomation({
      ...automation,
      consent: !automation.consent
    })
  }
  const onRulesChange = (rewardRules: RecognitionAutomationRule[]) => {
    setAutomation({
      ...automation,
      rewardRules
    })
  }
  const onRuleTypeChange = (ruleType: RecognitionAutomationRuleType) => {
    setAutomation({
      ...automation,
      ruleType,
      rewardRules: []
    })
  }
  const templatePreviewProps = {
    rewardMessage: automation.message,
    rewardPoints:
      automation.recognitionAutomationType === RecognitionAutomationType.RecognitionOnly ? null : automation.rewardRules?.[0]?.amount || 0,
    rewardTemplate: rewardTemplate
  }
  const mainActionBtnLbl =
    recognitionAutomation?.status === RecognitionAutomationStatus.Active
      ? "admin.automation.create.deactivateBtn"
      : "admin.automation.create.activateBtn"
  const mainActionStatusSwitch =
    recognitionAutomation?.status === RecognitionAutomationStatus.Active
      ? RecognitionAutomationStatus.Deactivated
      : RecognitionAutomationStatus.Active
  const headerLbl = recognitionAutomation?.uuid ? "admin.automation.editHeaderCountry" : "admin.automation.createHeader"
  const handleRecognitionAutomationTypeChange = (type: RecognitionAutomationType) => {
    setAutomation({
      ...automation,
      recognitionAutomationType: type,
      ...(recognitionAutomation?.automationType !== RecognitionAutomationType.Reward && type === RecognitionAutomationType.Reward
        ? emptyRewardAutomation
        : {})
    })
  }
  const businessUnitOptions = billingUnits
    ?.map((bu) => ({
      label: teamName(bu.team),
      value: bu.team.uuid
    }))
    .filter(({value}) => !alreadyCreatedRecognitionAutomations?.some(({regionId}) => regionId === value))
  const handleSenderNameOption = (newSenderNameOption: SenderNameOptions) => {
    setSenderNameOption(newSenderNameOption)
    setAutomation({
      ...automation,
      senderName: null
    })
  }
  const handleSenderNameChange = (newSenderName: string) => {
    setAutomation({
      ...automation,
      senderName: newSenderName
    })
  }

  const SENDER_NAME_OPTIONS: Option[] = [
    {
      value: SenderNameOptions.ADMIN_NAME,
      customNode: (
        <Body small>
          <FormattedMessage id="admin.automation.lbl.adminSender" />
        </Body>
      )
    },
    {
      value: SenderNameOptions.CUSTOM,
      customNode: (
        <Input
          name="senderName"
          type="text"
          placeholder="Type anything"
          hideLabel
          flush
          value={automation?.senderName}
          defaultValue={automation?.senderName}
          onChange={handleSenderNameChange}
          key={senderNameOption}
          maxLength={SENDER_NAME_MAX_LENGTH}
          validation={
            senderNameOption === SenderNameOptions.CUSTOM
              ? Validation.string(intl.formatMessage({id: "platform.defaultValidation.required"})).max(
                  SENDER_NAME_MAX_LENGTH,
                  intl.formatMessage({id: "platform.defaultValidation.maxLength"}, {count: intl.formatNumber(SENDER_NAME_MAX_LENGTH)})
                )
              : null
          }
          disabled={senderNameOption === SenderNameOptions.ADMIN_NAME}
        />
      )
    }
  ]
  const hasAvailableRegions = !!businessUnitOptions?.length
  return (
    <>
      <H2 marginBottom={1}>
        <FormattedMessage
          id={headerLbl}
          values={{automation: recognitionAutomation?.occasion?.name, country: teamName(recognitionAutomation?.region)}}
        />
      </H2>
      <Hr marginBottom={4} marginTop={4} />
      <Form>
        <Spacing marginBottom={4}>
          <H3 marginBottom={1}>
            <FormattedMessage id="admin.automation.automationTypeHeader" />
          </H3>
          <Body small>
            <FormattedMessage id="admin.automation.automationTypeSubtitle" />
          </Body>
          <Flex gap={2} marginTop={mainTheme.spacing.oneAndHalf}>
            <CardOption
              full
              selected={automation.recognitionAutomationType === RecognitionAutomationType.RecognitionOnly}
              onSelect={() => handleRecognitionAutomationTypeChange(RecognitionAutomationType.RecognitionOnly)}>
              <Body small weight={automation.recognitionAutomationType === RecognitionAutomationType.RecognitionOnly ? "bold" : "default"}>
                <FormattedMessage id="admin.automation.create.types.title.recognitionOnly" />
              </Body>
            </CardOption>
            <CardOption
              full
              selected={automation.recognitionAutomationType === RecognitionAutomationType.Reward}
              onSelect={() => handleRecognitionAutomationTypeChange(RecognitionAutomationType.Reward)}>
              <Body small weight={automation.recognitionAutomationType === RecognitionAutomationType.Reward ? "bold" : "default"}>
                <FormattedMessage id="admin.automation.create.types.title.reward" />
              </Body>
            </CardOption>
          </Flex>
        </Spacing>
        {businessUnitOptions && businessUnitOptions.length !== 1 && (
          <Spacing marginBottom={4}>
            <Select
              id="automation_selectCountry"
              label={formatMessage({id: "admin.components.businessUnits.countries.title"})}
              isClearable={false}
              isDisabled={recognitionAutomation?.uuid || !businessUnitOptions.length}
              value={automation.regionId}
              options={businessUnitOptions}
              validation={
                businessUnitOptions.length > 0 ? Validation.string(intl.formatMessage({id: "platform.defaultValidation.required"})) : null
              }
              onChange={handleSelectCountry}
            />
            {!businessUnitOptions.length && (
              <Error errorMsg={formatMessage({id: "admin.components.businessUnits.countries.notAvailable"})} />
            )}
          </Spacing>
        )}
        <H3 marginBottom={4}>
          <FormattedMessage id="admin.automation.addMessageSenderHeader" />
        </H3>
        <Flex marginBottom={4} marginTop={2}>
          <CustomRadio
            name="senderNameOption"
            label="admin.automation.lbl.addSender"
            gapRadios={1}
            value={senderNameOption}
            onChange={handleSenderNameOption}
            options={SENDER_NAME_OPTIONS}
            rowRenderer={CustomNodeRenderer}
          />
        </Flex>
        <Message
          label="admin.automation.create.message.label"
          placeholder="admin.automation.create.message.placeholder"
          name="messageBox"
          defaultValues={{
            message: recognitionAutomation?.message,
            companyValues: recognitionAutomation?.companyValues?.results,
            occasion: recognitionAutomation?.companyCelebration
          }}
          messageLength={TEMPLATE_MESSAGE_MAX_LENGTH}
          onChange={onMessageChange}
          validation={festiveTemplateSchema(intl)}
        />
        <Flex marginTop={2} justifyContent="flex-end">
          <Button cta onClick={() => setShowPreview(true)}>
            <FormattedMessage id="admin.automation.create.viewPreview.cta" />
          </Button>
        </Flex>
        {automation.recognitionAutomationType === RecognitionAutomationType.Reward && (
          <>
            <H3 marginBottom={4} marginTop={4}>
              <FormattedMessage id="admin.automation.rewardRulesHeader" />
            </H3>
            <UserBudgetSelector onWalletChange={onWalletChange} recognitionAutomation={recognitionAutomation} />
            <AutomationsRules
              rules={automation.rewardRules}
              onRulesChange={onRulesChange}
              ruleType={automation.ruleType}
              onRuleTypeChange={onRuleTypeChange}
            />
            <Hr marginBottom={4} marginTop={4} />
            <Checkbox
              name="check_confirmation"
              id="checkConfirmation"
              onChange={handleConfirmAutomation}
              checked={automation.consent}
              validation={Validation.bool().oneOf([true], formatMessage({id: "platform.defaultValidation.required"}))}>
              <FormattedMessage id="admin.automation.create.confirmSendPoints.checkbox" />
            </Checkbox>
            <Hr marginTop={4} />
          </>
        )}
        <Hidden
          value={!!recognitionAutomation || hasAvailableRegions}
          validation={Validation.bool().oneOf([true], formatMessage({id: "admin.automations.businessUnits.countries.notAvailable"}))}
        />
        <Flex gap={3} justifyContent="flex-end" marginTop={4}>
          <ValidationButton
            onClick={() => onSubmit(recognitionAutomation?.status || RecognitionAutomationStatus.Draft, true)}
            load={submit}>
            <FormattedMessage id="platform.button.save" />
          </ValidationButton>
          <ValidationButton primary onClick={() => onSubmit(mainActionStatusSwitch)} load={submit}>
            <FormattedMessage id={mainActionBtnLbl} />
          </ValidationButton>
        </Flex>
        {(createError || updateError) && (
          <Spacing marginTop={2}>
            <Error errorMsg={createErrorMsg || updateErrorMsg} error={createError || updateError} />
          </Spacing>
        )}
      </Form>
      <AutomationConfirmationModal
        recognitionAutomation={recognitionAutomationCreated || recognitionAutomationUpdated}
        operation={recognitionAutomationCreated ? Operation.CREATE : Operation.EDIT}
        isSave={isSaveOp}
      />
      <FullPreview {...templatePreviewProps} show={showPreview} onHide={() => setShowPreview(false)} />
    </>
  )
}
export default injectIntl(FormHandler)
